import { Button, FiltersIcon, Modal } from '@iheartjane/reefer';
import type { LabeledOption } from '@iheartjane/types';
import { useEffect, useState } from 'react';
import { useNavigation } from 'react-router';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { FilterActions } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/filterSelector/filterActions';
import {
  useBloomFilters,
  useProductSearchFilters,
  useSearchParamFilters,
} from '@jane/bloom/hooks/filters';
import type { SortOptions } from '@jane/bloom/types/schemas/algolia/product';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';
import { getFacetRange } from '@jane/bloom/viewModels/facetRange';
import type {
  FilterTypeKeys,
  ProductSearchFiltersViewModel,
} from '@jane/bloom/viewModels/productSearchFilters';

import styles from './allFiltersModal.module.css';
import { ModalButtonFilterSection } from './allFiltersModal/buttonFilterSection';
import {
  ModalFilterEntrypoint,
  ModalFilterSection,
} from './allFiltersModal/modalFiltersSection';
import { PotencySection } from './allFiltersModal/potencySection';
import { RatingSection } from './allFiltersModal/ratingSection';
import { SortSection } from './allFiltersModal/sortSection';
import { JaneGoldSection } from './allFiltersModal/specialsSection';

interface FiltersButtonProps {
  availableProductsCount?: number;
  currentSort: SortOptions;
  hasJaneGoldProducts?: boolean;
  shouldShowGold?: boolean;
  sortOptions: LabeledOption<SortOptions>[];
}

export function AllFiltersModal({
  availableProductsCount,
  currentSort,
  hasJaneGoldProducts,
  shouldShowGold,
  sortOptions,
}: FiltersButtonProps) {
  const {
    getPotencyFilterKeysNonEmpty,
    getProductSearchFilterOptions,
    productSearchFilters,
  } = useProductSearchFilters();
  const { sortedAllDisplayFilterKey } = useBloomFilters();
  const { clearAllFilters, searchParams, setSearchParams } =
    useSearchParamFilters();
  const [resetting, setResetting] = useState(false);
  const { state } = useNavigation();

  const [originalFilters, setOriginalFilters] = useState({});

  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('default');

  const validPotencySliderKeys = getPotencyFilterKeysNonEmpty().filter(
    (key) => {
      const filterList = getProductSearchFilterOptions(key);
      const { rangeMax, rangeMin } = getFacetRange(filterList);
      return rangeMax > rangeMin;
    }
  );

  const saleItem = getProductSearchFilterOptions('categories').find(
    (option) => option.value === 'sale'
  );

  const sections = sortedAllDisplayFilterKey
    .filter(
      (filterKey) =>
        [
          'activities',
          'ratings',
          'potencies',
          'prices',
          'weights',
          'specials',
          'terpenes',
        ].includes(filterKey) ||
        productSearchFilters?.[filterKey as keyof ProductSearchFiltersViewModel]
          ?.length ||
        -1 > 0
    )
    .map((filterKey) => {
      switch (filterKey) {
        case 'ratings': {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <RatingSection key={filterKey} />
            )
          );
        }
        case 'potencies': {
          return (
            validPotencySliderKeys.length > 0 && (
              <PotencySection key={filterKey} resetting={resetting} />
            )
          );
        }
        case 'prices': {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <ModalButtonFilterSection key={filterKey} filterKey={filterKey} />
            )
          );
        }
        case 'brands': {
          return (
            !isEmpty(productSearchFilters?.['brands']) && (
              <ModalFilterEntrypoint
                key={filterKey}
                filterKey={filterKey}
                handleClick={() => setSelectedTab('brands')}
              />
            )
          );
        }
        case 'terpenes': {
          return (
            !isEmpty(productSearchFilters?.['terpenes']) && (
              <ModalFilterEntrypoint
                key={filterKey}
                filterKey={filterKey}
                handleClick={() => setSelectedTab('terpenes')}
              />
            )
          );
        }
        case 'jane-gold': {
          return false;
        }
        default: {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <ModalButtonFilterSection
                shouldShowGold={shouldShowGold}
                key={filterKey}
                filterKey={filterKey as FilterTypeKeys}
                icon
                shouldShowDividerBelow={
                  sortedAllDisplayFilterKey[
                    sortedAllDisplayFilterKey.length - 1
                  ] !== filterKey
                }
              />
            )
          );
        }
      }
    });

  useEffect(() => {
    if (resetting && state === 'idle') {
      setResetting(false);
    }
  }, [resetting, state]);

  const renderView = () => {
    switch (selectedTab) {
      case 'brands':
        return (
          <ModalFilterSection selectedTab={selectedTab} resetting={resetting} />
        );
      case 'terpenes':
        return (
          <ModalFilterSection selectedTab={selectedTab} resetting={resetting} />
        );
      default:
        return (
          <>
            <Modal.Header
              className={styles['modalHeader']}
              title="Filters and sort"
              textAlign="center"
            />
            <Modal.Content>
              <SortSection
                sortOptions={sortOptions}
                currentSort={currentSort}
              />
              {shouldShowGold && hasJaneGoldProducts && (
                <JaneGoldSection
                  hasJaneGoldProducts={hasJaneGoldProducts}
                  saleItem={saleItem}
                />
              )}
              {sections}
            </Modal.Content>
          </>
        );
    }
  };

  return (
    <>
      <Button.Icon
        mr={8}
        variant="tertiary"
        icon={<FiltersIcon />}
        label="All filters"
        key="allFilters"
        ariaLabel="All Filters"
        onClick={() => {
          setOriginalFilters(searchParams);
          setOpen(true);
        }}
      />
      <ClientOnly>
        {(isMobile) => (
          <Modal
            background="grays-white"
            open={open}
            onRequestClose={
              selectedTab === 'default'
                ? () => {
                    setOpen(false);
                    setSearchParams(originalFilters);
                  }
                : () => setSelectedTab('default')
            }
            id="parent"
            variant={isMobile ? 'full-screen' : 'standard'}
            overlayClose
          >
            {renderView()}
            <Modal.Footer>
              <FilterActions
                availableProductsCount={availableProductsCount}
                fullButtons={{ clear: true, show: true }}
                handleClearAll={clearAllFilters}
                handleClose={() => setOpen(false)}
              />
            </Modal.Footer>
          </Modal>
        )}
      </ClientOnly>
    </>
  );
}
